import { useState, useEffect } from 'react';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { MdLanguage } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useBreadcrumbLabel } from '../../hooks/useBreadcrumbLabel';
import i18n, { ADMIN_LANGUAGES } from '../../i18n';

/** Compact language switcher for the admin navbar (English / Українська / Deutsch). */
function LanguageMenu() {
  const { i18n: i18nInstance } = useTranslation();
  const current = i18nInstance.language;
  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Change language"
        icon={<MdLanguage size={20} />}
        variant="ghost"
        size="sm"
        data-testid="admin-language-menu"
      />
      <MenuList minW="160px">
        {ADMIN_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            fontWeight={current === lang.code ? '700' : '400'}
            onClick={() => i18n.changeLanguage(lang.code)}
            data-testid={`admin-language-${lang.code}`}
          >
            {lang.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

export interface NavbarAdminProps {
  brandText: string;
  onOpen: () => void;
}

/**
 * Resolves a breadcrumb label via the hook. Must be its own component
 * so it can call the hook, but only returns a text node — the wrapping
 * BreadcrumbItem lives in the parent so Chakra can inject separators.
 */
function BreadcrumbLabel({
  segment,
  parentSegment,
}: {
  segment: string;
  parentSegment: string | undefined;
}) {
  const { label } = useBreadcrumbLabel(segment, parentSegment);
  return <>{label}</>;
}

/**
 * Renders the large heading text using the last segment's resolved label.
 */
function BrandHeading({
  segment,
  parentSegment,
}: {
  segment: string;
  parentSegment: string | undefined;
}) {
  const mainText = useColorModeValue('navy.700', 'white');
  const { label } = useBreadcrumbLabel(segment, parentSegment);

  return (
    <Link
      color={mainText}
      href="#"
      bg="inherit"
      borderRadius="inherit"
      fontWeight="bold"
      fontSize="34px"
      _hover={{ color: mainText }}
      _active={{
        bg: 'inherit',
        transform: 'none',
        borderColor: 'transparent',
      }}
      _focus={{ boxShadow: 'none' }}
    >
      {label}
    </Link>
  );
}

export function NavbarAdmin({ brandText, onOpen }: NavbarAdminProps) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 1);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const secondaryText = useColorModeValue('gray.700', 'white');
  const mainText = useColorModeValue('navy.700', 'white');
  const navbarBg = useColorModeValue(
    'rgba(244, 247, 254, 0.2)',
    'rgba(11,20,55,0.5)',
  );

  // Build path segments: e.g. /library/abc-123 → ['library', 'abc-123']
  // Skip 'dashboard' — it's already the root breadcrumb
  const segments = location.pathname.split('/').filter((s) => s && s !== 'dashboard');

  return (
    <Box
      position="fixed"
      boxShadow={scrolled ? 'sm' : 'none'}
      bg={navbarBg}
      borderColor="transparent"
      filter="none"
      backdropFilter="blur(20px)"
      backgroundPosition="center"
      backgroundSize="cover"
      borderRadius="16px"
      borderWidth="1.5px"
      borderStyle="solid"
      transitionDelay="0s, 0s, 0s, 0s"
      transitionDuration="0.25s, 0.25s, 0.25s, 0s"
      transitionProperty="box-shadow, background-color, filter, border"
      transitionTimingFunction="linear, linear, linear, linear"
      alignItems={{ xl: 'center' }}
      display="flex"
      minH="75px"
      justifyContent={{ xl: 'center' }}
      lineHeight="25.6px"
      mx="auto"
      pb="8px"
      right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
      px={{ sm: '15px', md: '10px' }}
      ps={{ xl: '0px' }}
      pt="8px"
      top={{ base: '12px', md: '16px', lg: '20px', xl: '20px' }}
      w={{
        base: 'calc(100vw - 6%)',
        md: 'calc(100vw - 8%)',
        lg: 'calc(100vw - 6%)',
        xl: 'calc(100vw - 345px)',
        '2xl': 'calc(100vw - 345px)',
      }}
      zIndex="sticky"
    >
      <Flex
        w="100%"
        flexDirection={{ sm: 'column', md: 'row' }}
        alignItems={{ xl: 'center' }}
      >
        <Flex align="center">
          <IconButton
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            variant="ghost"
            display={{ base: 'flex', xl: 'none' }}
            onClick={onOpen}
            mr={3}
            data-testid="navbar-hamburger"
          />
          <Box mb={{ sm: '8px', md: '0px' }}>
            <Breadcrumb separator="/">
              <BreadcrumbItem color={secondaryText} fontSize="sm" mb="5px">
                <BreadcrumbLink as={RouterLink} to="/dashboard" color={secondaryText}>
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              {segments.map((seg, idx) => {
                const isLast = idx === segments.length - 1;
                const parentSeg = idx > 0 ? segments[idx - 1] : undefined;
                // Use navigate(-1) for the direct parent so filters/search
                // params are preserved via browser history.
                const goUp = () => navigate(-1);
                return (
                  <BreadcrumbItem
                    key={`/${segments.slice(0, idx + 1).join('/')}`}
                    color={secondaryText}
                    fontSize="sm"
                    mb="5px"
                    isCurrentPage={isLast}
                  >
                    {isLast ? (
                      <BreadcrumbLink href="#" color={secondaryText}>
                        <BreadcrumbLabel segment={seg} parentSegment={parentSeg} />
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbLink
                        as="button"
                        onClick={goUp}
                        color={secondaryText}
                      >
                        <BreadcrumbLabel segment={seg} parentSegment={parentSeg} />
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                );
              })}
            </Breadcrumb>
            {segments.length > 0 ? (
              <BrandHeading
                segment={segments[segments.length - 1]}
                parentSegment={segments.length > 1 ? segments[segments.length - 2] : undefined}
              />
            ) : (
              <Link
                color={mainText}
                href="#"
                bg="inherit"
                borderRadius="inherit"
                fontWeight="bold"
                fontSize="34px"
                _hover={{ color: mainText }}
                _active={{ bg: 'inherit', transform: 'none', borderColor: 'transparent' }}
                _focus={{ boxShadow: 'none' }}
              >
                {brandText}
              </Link>
            )}
          </Box>
        </Flex>
        <Flex ms="auto" align="center">
          <LanguageMenu />
        </Flex>
      </Flex>
    </Box>
  );
}
